import { Head, useForm } from '@inertiajs/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GraduationCap, Paperclip, Send } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { TextArea } from '@/components/ui/TextArea'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'
import { cn } from '@/lib/cn'
import { useChatroomChannel, type ChatroomPayload } from '@/lib/useChatroomChannel'
import { FieldError } from '@/components/feedback/FieldError'

type Chatroom = {
  id: string
  playerName: string
  teamName?: string | null
  playerPrefecture?: string | null
}

type Message = {
  id: string
  content: string
  createdAt: string
  senderType: string
  senderName: string
  isMine: boolean
}

export type SchoolChatroomShowProps = {
  chatroom: Chatroom
  messages: Message[]
  breadcrumbs: Breadcrumb[]
}

export default function SchoolChatroomShow({ chatroom, messages, breadcrumbs }: SchoolChatroomShowProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [chatMessages, setChatMessages] = useState(messages)
  const { data, setData, post, processing, errors, transform } = useForm({
    content: '',
  })

  useEffect(() => {
    setChatMessages(messages)
  }, [messages])

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight
    }
  }, [chatMessages])

  const handleBroadcast = useCallback((payload: ChatroomPayload) => {
    setChatMessages((prev) => {
      if (prev.some((message) => message.id === payload.message.id)) {
        return prev
      }

      return [
        ...prev,
        {
          ...payload.message,
          isMine: payload.message.senderType === 'SchoolCoach',
        },
      ]
    })
  }, [])

  useChatroomChannel(chatroom.id, handleBroadcast)

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!data.content.trim()) return

    transform((data) => ({ chat_message: data }))
    post(`/school/chatrooms/${chatroom.id}/messages`, {
      preserveScroll: true,
      onSuccess: () => setData('content', ''),
    })
  }

  return (
    <AppShell>
      <Head title={`${chatroom.playerName} とのチャット`} />
      <PageHeader
        title={`${chatroom.playerName} とのチャット`}
        description="コミュニケーションの履歴を確認し、次のアクションに繋げましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <></>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="glass-panel relative flex h-[70vh] flex-col overflow-hidden border border-white/10 bg-white/5 p-0">
          <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-white">{chatroom.playerName}</h2>
              <p className="text-xs text-white/50">
                {(chatroom.teamName ?? '所属未登録') + ' · ' + (chatroom.playerPrefecture ?? '所在地未登録')}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20 text-brand-100">
              <GraduationCap className="h-5 w-5" />
            </div>
          </div>

          <div ref={viewportRef} className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
            {chatMessages.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-6 text-sm text-white/60">
                最初のメッセージを送り、面談の提案や質問を投げかけてみましょう。
              </div>
            ) : (
              chatMessages.map((message) => (
                <div key={message.id} className="flex flex-col gap-1">
                  <span
                    className={cn(
                      'text-xs text-white/50',
                      message.isMine ? 'self-end text-right' : 'self-start text-left',
                    )}
                  >
                    {message.isMine ? 'あなた' : message.senderName}
                  </span>
                  <div
                    className={cn(
                      'max-w-xl whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-soft',
                      message.isMine
                        ? 'self-end bg-brand-500 text-white'
                        : 'self-start border border-white/10 bg-white/10 text-white/90 backdrop-blur',
                    )}
                  >
                    {message.content}
                  </div>
                  <span className={cn('text-xs text-white/40', message.isMine ? 'self-end text-right' : 'self-start')}>
                    {formatTimestamp(message.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>

          <form onSubmit={submit} className="border-t border-white/5 bg-surface-900/80 px-6 py-4">
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="sm" disabled>
                <Paperclip className="h-4 w-4" />
              </Button>
              <TextArea
                autoResize
                value={data.content}
                onChange={(event) => setData('content', event.target.value)}
                placeholder="メッセージを入力..."
                className="flex-1 resize-none"
                rows={2}
              />
              <Button type="submit" disabled={processing || !data.content.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <FieldError message={errors.content} />
          </form>
        </Card>

        <Card className="space-y-4">
          <h2 className="text-lg font-semibold text-white">コミュニケーションのコツ</h2>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              返信が途切れている場合は、次のステップ（訪問日程や必要書類）を具体的に提示すると返答率が上がります。
            </li>
            <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              面談前に選手の希望条件を確認しておくと、当日の擦り合わせがスムーズになります。
            </li>
          </ul>
        </Card>
      </div>
    </AppShell>
  )
}

function formatTimestamp(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
